<template>
  <div class="expressed-as" ref="containerRef" v-click-outside="closeDropdown">
    <label v-if="label" class="expressed-as__label">{{ label }}</label>
    <div
      class="expressed-as__input"
      :class="{
        'expressed-as__input--open': isOpen,
        'expressed-as__input--locked': locked,
      }"
      @click="handleInputClick"
      @keydown.enter.prevent="handleInputClick"
      @keydown.space.prevent="handleInputClick"
      @keydown.escape="closeDropdown"
      :tabindex="locked ? -1 : 0"
      role="combobox"
      :aria-expanded="String(isOpen)"
      aria-haspopup="listbox"
      :aria-disabled="String(locked)"
      :aria-label="label || 'Pay Basis'"
    >
      <div class="expressed-as__content">
        <span class="expressed-as__value">{{ selectedLabel }}</span>
      </div>
      <v-icon
        v-if="locked"
        class="expressed-as__icon expressed-as__icon--lock"
        size="20"
        aria-hidden="true"
      >
        mdi-lock
      </v-icon>
      <v-icon
        v-else
        class="expressed-as__icon expressed-as__icon--chevron"
        :class="{ 'expressed-as__icon--rotated': isOpen }"
        size="20"
        aria-hidden="true"
      >
        mdi-chevron-down
      </v-icon>
    </div>

    <div
      v-if="isOpen && !locked"
      class="expressed-as__menu"
      role="listbox"
      :aria-label="`Select ${label || 'pay basis'}`"
    >
      <div
        v-for="option in options"
        :key="option.value"
        class="expressed-as__menu-item"
        :class="{
          'expressed-as__menu-item--selected': modelValue === option.value,
        }"
        @click="selectOption(option.value)"
        @keydown.enter.prevent="selectOption(option.value)"
        :tabindex="0"
        role="option"
        :aria-selected="modelValue === option.value"
      >
        <span class="expressed-as__menu-label">{{ option.label }}</span>
        <v-icon
          v-if="modelValue === option.value"
          class="expressed-as__menu-check"
          size="16"
          aria-hidden="true"
        >
          mdi-check
        </v-icon>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";

const props = defineProps({
  modelValue: {
    type: String,
    required: true,
    validator: (value) => ["per_hour", "per_month", "per_year"].includes(value),
  },
  locked: {
    type: Boolean,
    default: false,
  },
  label: {
    type: String,
    default: "Pay Basis",
  },
});

const emit = defineEmits(["update:modelValue"]);

const containerRef = ref(null);
const isOpen = ref(false);

const options = [
  { value: "per_hour", label: "Hourly" },
  { value: "per_month", label: "Monthly" },
  { value: "per_year", label: "Annually" },
];

const selectedLabel = computed(() => {
  const option = options.find((opt) => opt.value === props.modelValue);
  return option ? option.label : "Annually";
});

const handleInputClick = () => {
  if (props.locked) {
    return;
  }
  isOpen.value = !isOpen.value;
};

const closeDropdown = () => {
  isOpen.value = false;
};

const selectOption = (value) => {
  emit("update:modelValue", value);
  closeDropdown();
};
</script>

<style scoped>
.expressed-as {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-width: 352px;
  position: relative;
}

.expressed-as__label {
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #344054;
}

.expressed-as__input {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 14px;
  background: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  cursor: pointer;
  min-height: 44px;
  transition: border-color 0.2s;
}

.expressed-as__input:hover:not(.expressed-as__input--locked) {
  border-color: #b0b8c4;
}

.expressed-as__input:focus-visible {
  outline: none;
  border-color: #d6bbfb;
  box-shadow: 0 0 0 4px rgba(214, 187, 251, 0.24);
}

.expressed-as__input--open {
  border-color: #d6bbfb;
}

.expressed-as__input--locked {
  background: #e0e0e0;
  cursor: not-allowed;
  border-color: #d0d5dd;
}

.expressed-as__content {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.expressed-as__value {
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: #101828;
}

.expressed-as__input--locked .expressed-as__value {
  color: #4d4d4d;
}

.expressed-as__icon {
  flex-shrink: 0;
  color: #667085;
}

.expressed-as__icon--lock {
  color: #667085;
}

.expressed-as__icon--chevron {
  transition: transform 0.2s;
}

.expressed-as__icon--rotated {
  transform: rotate(180deg);
}

.expressed-as__menu {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  box-shadow: 0px 4px 6px -2px rgba(16, 24, 40, 0.03),
    0px 12px 16px -4px rgba(16, 24, 40, 0.08);
  padding: 4px 0;
  z-index: 1000;
}

.expressed-as__menu-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  cursor: pointer;
  transition: background-color 0.15s;
}

.expressed-as__menu-item:hover {
  background-color: #f9fafb;
}

.expressed-as__menu-item:focus-visible {
  outline: none;
  background-color: #f9fafb;
}

.expressed-as__menu-item--selected {
  background-color: #f9f5ff;
}

.expressed-as__menu-label {
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #344054;
}

.expressed-as__menu-check {
  color: #7f56d9;
  flex-shrink: 0;
}
</style>
